import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import type { ExploreQuery } from '~/data/templates'
import { dialogPath, type DialogName } from '~/lib/dialogs'

/**
 * The way into a page that opens over this one. It stays on the page it is pressed from and
 * names the dialog in that page's search parameters, which is what keeps the page underneath
 * mounted — see the web app's `specs/tech.spec.md`.
 *
 * The address that names it is a mask over the real one, and the mask lives here rather than
 * at each link, so there is no way to open one of these and leave the URL saying otherwise.
 */
export function DialogLink({
  dialog,
  day,
  measure,
  templateId,
  explore,
  className,
  label,
  onClick,
  children,
}: {
  dialog: DialogName
  /** The day a weigh-in panel opens on. Omitted means today, and today is never written. */
  day?: string
  /** Which of the body's readings the body panel opens on. */
  measure?: string
  /** Which template a template's page is about, which is the one of these that is about one. */
  templateId?: string
  /**
   * What Explore was showing, on a link out of Explore. It is what the template's page reads to
   * know it was opened from there, and what its way back puts back — written even when nothing
   * has been narrowed, because "opened from Explore having touched no filter" is still opened
   * from Explore, and a way back that led to the library instead would be the wrong door.
   */
  explore?: ExploreQuery
  className?: string
  /** The accessible name, where the contents are a mark rather than words. */
  label?: string
  onClick?: () => void
  children: ReactNode
}) {
  return (
    <Link
      to="."
      // Whatever day the URL held belongs to the panel being left, not the one being opened.
      search={({ day: _left, measure: _read, template: _about, ...prev }) => ({
        ...prev,
        dialog,
        ...(day === undefined ? {} : { day }),
        ...(measure === undefined ? {} : { measure }),
        ...(templateId === undefined ? {} : { template: templateId }),
        ...(explore === undefined ? {} : { explore }),
      })}
      mask={{ to: dialogPath(dialog, templateId) }}
      resetScroll={false}
      // No root view transition: the dialog eases itself in over a page that never moved,
      // and a snapshot pass would let named tiles paint their overflow outside their strips.
      viewTransition={false}
      className={className}
      {...(label === undefined ? {} : { 'aria-label': label })}
      {...(onClick === undefined ? {} : { onClick })}
    >
      {children}
    </Link>
  )
}
